import { produce } from 'immer'

import { WBMessageType, WorkflowBuilderState } from './workflow-builder.state'

const onGetResponseStream = (
  state: WorkflowBuilderState,
  { response }: { response: any }
): WorkflowBuilderState =>
  produce(state, (draft: WorkflowBuilderState) => {
    draft.streamingAiResponse = true
    draft.pendingAiResponse = true

    const lastMessageDraft = draft.messages[draft.messages.length - 1]
    const isLastMessageOfTypeAi =
      lastMessageDraft &&
      lastMessageDraft?.type === WBMessageType.AiMsg &&
      !lastMessageDraft?.commited
    const { content, reasoning, streaming, chunk, type, error, pass_to_human } = response

    if (error) {
      return
    } else if (type === 'patch_tool_calls') {
      draft.pendingRequestActionMessage = 'Patching tool calls.'
    } else if (type === 'summarization') {
      draft.pendingRequestActionMessage = 'Summarizing.'
    } else if (type === 'tool_call_chunk') {
      const chunkData = typeof chunk === 'string' ? JSON.parse(chunk) : chunk
      const toolName = chunkData?.name || content?.name || '...'
      draft.pendingRequestActionMessage = 'Tool calling ' + toolName
    } else if (type === 'text') {
      draft.pendingRequestActionMessage = 'Preparing response.'
      if (isLastMessageOfTypeAi) {
        if (lastMessageDraft.id === response.id) {
          if (streaming) {
            lastMessageDraft.message += content.text
          }
          lastMessageDraft.type = WBMessageType.AiMsg
        } else {
          draft.messages.push({
            ...response,
            message: content.text,
            pass_to_human: pass_to_human ?? false,
            type: WBMessageType.AiMsg,
          })
        }
      } else {
        draft.messages.push({
          ...response,
          message: content.text,
          pass_to_human: pass_to_human ?? false,
          type: WBMessageType.AiMsg,
        })
      }
    } else if (type === 'interrupt') {
      draft.pendingRequestActionMessage = 'Require user input.'
      draft.messages.push({
        ...response,
        message: content,
        type: WBMessageType.InterruptMsg,
      })
    } else if (type === 'reasoning') {
      draft.pendingRequestActionMessage = 'Reasoning.'
      const reasoningText = reasoning || content?.text || content
      if (isLastMessageOfTypeAi) {
        if (!lastMessageDraft.reasoning) {
          lastMessageDraft.reasoning = []
        }
        if (Array.isArray(lastMessageDraft.reasoning)) {
          lastMessageDraft.reasoning.push(reasoningText)
        }
      } else {
        draft.messages.push({
          ...response,
          reasoning: [reasoningText],
          message: '',
          type: WBMessageType.AiMsg,
        })
      }
    } else if (type === 'tool_call') {
      const toolCallData = typeof chunk === 'string' ? JSON.parse(chunk) : chunk

      if (toolCallData.type === 'text' && !toolCallData.name) {
        draft.pendingRequestActionMessage = 'Processing tool response.'
        return
      }

      draft.pendingRequestActionMessage = 'Tool calling ' + (toolCallData.name || '...')
      if (isLastMessageOfTypeAi) {
        if (!lastMessageDraft.tool_calls) {
          lastMessageDraft.tool_calls = []
        }
        if (Array.isArray(lastMessageDraft.tool_calls)) {
          if (toolCallData.id) {
            const existingIndex = lastMessageDraft.tool_calls.findIndex(
              (tc: any) => tc.id === toolCallData.id
            )
            if (existingIndex >= 0) {
              lastMessageDraft.tool_calls[existingIndex] = {
                ...lastMessageDraft.tool_calls[existingIndex],
                ...toolCallData,
                name: toolCallData.name || lastMessageDraft.tool_calls[existingIndex].name,
              }
            } else {
              lastMessageDraft.tool_calls.push(toolCallData)
            }
          } else if (toolCallData.name) {
            lastMessageDraft.tool_calls.push(toolCallData)
          }
        }
      } else {
        draft.messages.push({
          id: response.id,
          reasoning: [],
          tool_calls: [toolCallData],
          message: '',
          type: WBMessageType.AiMsg,
        })
      }
    } else if (type === 'tool_result') {
      draft.pendingRequestActionMessage = 'Tool Responded.'
      const resultId = content?.tool_call_id
      const resultToolName = content?.tool_name
      if (isLastMessageOfTypeAi && lastMessageDraft.tool_calls) {
        lastMessageDraft.tool_calls = lastMessageDraft.tool_calls.map((toolCall: any) => {
          if (resultId && toolCall.id === resultId) {
            return { ...toolCall, result: content?.result, name: toolCall.name || resultToolName }
          }
          return toolCall
        })
        if (resultToolName && resultId) {
          const hasMatch = lastMessageDraft.tool_calls.some((tc: any) => tc.id === resultId)
          if (!hasMatch) {
            const idx = [...lastMessageDraft.tool_calls]
              .reverse()
              .findIndex((tc: any) => tc.name === resultToolName && !tc.result)
            if (idx >= 0) {
              const realIdx = lastMessageDraft.tool_calls.length - 1 - idx
              lastMessageDraft.tool_calls[realIdx] = {
                ...lastMessageDraft.tool_calls[realIdx],
                result: content?.result,
              }
            }
          }
        }
      }
    } else if (type === 'tool_response') {
      draft.pendingRequestActionMessage = 'Tool Responded.'
      const updated_tool_calls = lastMessageDraft.tool_calls.map((toolCall: any) => {
        if (toolCall.id === chunk.tool_call_id) {
          toolCall.response = chunk.content
        }
        return toolCall
      })
      lastMessageDraft.tool_calls = updated_tool_calls
    } else if (type === 'workflow_saved') {
      draft.pendingRequestActionMessage = null
      draft.pendingAiResponse = false
      draft.streamingAiResponse = false
      draft.messages.push({
        ...response,
        message: content,
        type: WBMessageType.WorkflowSavedMsg,
      })
    } else if (type === 'workflow_test_start') {
      draft.pendingRequestActionMessage = 'Testing workflow...'
      draft.messages.push({
        ...response,
        message: content,
        type: WBMessageType.WorkflowTestMsg,
      })
    } else if (type === 'workflow_test_event') {
      draft.pendingRequestActionMessage = 'Running workflow test...'
      const lastMsg = draft.messages[draft.messages.length - 1]
      if (lastMsg && lastMsg.type === WBMessageType.WorkflowTestMsg) {
        const msgData = lastMsg.message as any
        if (!msgData?.events) {
          lastMsg.message = { ...(typeof msgData === 'object' ? msgData : {}), events: [] } as any
        }
        (lastMsg.message as any).events.push(content)
      }
    } else if (type === 'workflow_test_complete') {
      draft.pendingRequestActionMessage = null
      draft.pendingAiResponse = false
      draft.streamingAiResponse = false
      const lastTestMsg = draft.messages[draft.messages.length - 1]
      if (lastTestMsg && lastTestMsg.type === WBMessageType.WorkflowTestMsg) {
        const msgData = lastTestMsg.message as any
        lastTestMsg.message = {
          ...(typeof msgData === 'object' ? msgData : {}),
          ...content,
          completed: true,
        } as any
      }
    } else if (type === 'workflow_test_skipped' || type === 'workflow_test_error') {
      draft.pendingRequestActionMessage = null
      draft.pendingAiResponse = false
      draft.streamingAiResponse = false
    }
  })

const onUserSendMessage = (
  state: WorkflowBuilderState,
  { message, thread_id }: { message: string; thread_id: string }
): WorkflowBuilderState =>
  produce(state, (draft: WorkflowBuilderState) => {
    draft.pendingAiResponse = true
    draft.streamingAiResponse = false
    draft.pendingRequestActionMessage = 'Thinking...'
    draft.thread_id = thread_id
    draft.previousUserMessage = message
    draft.messages.push({
      message,
      type: WBMessageType.UserMsg,
    })
  })

const onGetResponseFailure = (state: WorkflowBuilderState): WorkflowBuilderState =>
  produce(state, (draft: WorkflowBuilderState) => {
    draft.pendingAiResponse = false
    draft.streamingAiResponse = false
    draft.pendingRequestActionMessage = null
  })

const onGetResponseStreamCompleted = (state: WorkflowBuilderState): WorkflowBuilderState =>
  produce(state, (draft: WorkflowBuilderState) => {
    draft.pendingAiResponse = false
    draft.streamingAiResponse = false
    draft.pendingRequestActionMessage = null
    const lastMessage = draft.messages[draft.messages.length - 1]
    if (lastMessage && lastMessage.type === WBMessageType.AiMsg) {
      lastMessage.commited = true
    }
  })

const onClearMessages = (state: WorkflowBuilderState): WorkflowBuilderState =>
  produce(state, (draft: WorkflowBuilderState) => {
    draft.messages = []
    draft.pendingAiResponse = false
    draft.streamingAiResponse = false
    draft.pendingRequestActionMessage = null
    draft.previousUserMessage = null
  })

const onAddInitialMessage = (
  state: WorkflowBuilderState,
  { initialMessage }: { initialMessage: string }
): WorkflowBuilderState =>
  produce(state, (draft: WorkflowBuilderState) => {
    draft.messages.push({ message: initialMessage, type: WBMessageType.AiMsg })
  })

export {
  onGetResponseStream,
  onUserSendMessage,
  onGetResponseFailure,
  onGetResponseStreamCompleted,
  onClearMessages,
  onAddInitialMessage,
}
